import {
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { GripHorizontal } from 'lucide-react'
import { cn } from '@/utils'
import {
  PEZ_EFFECTS_LIST_RADIUS,
  PEZ_EFFECTS_LIST_RESIZE_HANDLE_HEIGHT,
  PEZ_EFFECTS_LIST_WIDTH,
  PEZ_EFFECTS_VIEW_WIDTH,
} from '@/components/app/photos/photo-effects/constants/photo-effect-row-layout'
import {
  clampPezListViewportHeight,
  getPezDefaultListViewportHeight,
} from '@/components/app/photos/photo-effects/helpers/photo-effects-list-resize'

export type PhotoEffectsListViewportProps = {
  children: ReactNode
  className?: string
  /** Notifies parent while the list viewport is being vertically resized. */
  onResizingChange?: (isResizing: boolean) => void
}

/**
 * Scrollable PEZ effects frame with a bottom grip to resize vertically (Figma 113-7446).
 * Pointer-drag resize — shadcn Resizable is not in the repo yet.
 */
export function PhotoEffectsListViewport({
  children,
  className,
  onResizingChange,
}: PhotoEffectsListViewportProps) {
  const [viewportHeight, setViewportHeight] = useState(getPezDefaultListViewportHeight)
  const [isResizing, setIsResizing] = useState(false)
  const isResizingRef = useRef(false)
  const resizeStartRef = useRef({ pointerY: 0, height: getPezDefaultListViewportHeight() })
  const viewportHeightRef = useRef(viewportHeight)
  const cleanupRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    viewportHeightRef.current = viewportHeight
  }, [viewportHeight])

  useEffect(() => {
    return () => {
      cleanupRef.current?.()
      cleanupRef.current = null
    }
  }, [])

  const endResize = useCallback(() => {
    if (!isResizingRef.current) return

    isResizingRef.current = false
    setIsResizing(false)
    onResizingChange?.(false)
    document.body.style.removeProperty('user-select')
    document.body.style.removeProperty('cursor')
    cleanupRef.current?.()
    cleanupRef.current = null
  }, [onResizingChange])

  const applyResizeDelta = useCallback((clientY: number) => {
    if (!isResizingRef.current) return

    const deltaY = clientY - resizeStartRef.current.pointerY

    setViewportHeight(clampPezListViewportHeight(resizeStartRef.current.height + deltaY))
  }, [])

  const handleResizeStart = (e: ReactPointerEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()

    cleanupRef.current?.()

    isResizingRef.current = true
    setIsResizing(true)
    onResizingChange?.(true)
    resizeStartRef.current = { pointerY: e.clientY, height: viewportHeightRef.current }
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'ns-resize'

    const onMove = (moveEvent: PointerEvent) => {
      moveEvent.preventDefault()
      applyResizeDelta(moveEvent.clientY)
    }

    const onUp = () => {
      endResize()
    }

    window.addEventListener('pointermove', onMove, { capture: true })
    window.addEventListener('pointerup', onUp, { capture: true })
    window.addEventListener('pointercancel', onUp, { capture: true })

    cleanupRef.current = () => {
      window.removeEventListener('pointermove', onMove, { capture: true })
      window.removeEventListener('pointerup', onUp, { capture: true })
      window.removeEventListener('pointercancel', onUp, { capture: true })
    }

    e.currentTarget.setPointerCapture?.(e.pointerId)
  }

  const handleResizePointerMove = (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (!isResizingRef.current) return
    e.preventDefault()
    e.stopPropagation()
    applyResizeDelta(e.clientY)
  }

  const handleResizePointerEnd = (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (!isResizingRef.current) return
    e.preventDefault()
    e.stopPropagation()
    endResize()

    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
  }

  return (
    <div
      className={cn('box-border shrink-0', className)}
      style={{ width: PEZ_EFFECTS_VIEW_WIDTH }}
      data-photo-effects-list-viewport
    >
      <div
        className="box-border overflow-y-auto overflow-x-hidden border border-[#D4D4D4]"
        style={{
          width: PEZ_EFFECTS_VIEW_WIDTH,
          height: viewportHeight,
        }}
        data-photo-effects-list-frame
        data-photo-effects-list-viewport-height={viewportHeight}
      >
        <div
          className="box-border bg-white"
          style={{
            width: PEZ_EFFECTS_LIST_WIDTH,
            borderRadius: PEZ_EFFECTS_LIST_RADIUS,
          }}
          data-photo-effects-list
        >
          {children}
        </div>
      </div>

      <button
        type="button"
        aria-label="Resize photo effects list"
        data-photo-effects-list-resize-handle
        onPointerDown={handleResizeStart}
        onPointerMove={handleResizePointerMove}
        onPointerUp={handleResizePointerEnd}
        onPointerCancel={handleResizePointerEnd}
        className={cn(
          'touch-none flex w-full cursor-ns-resize items-center justify-center border-none bg-transparent p-0 text-[#737373] hover:bg-black/[0.04]',
          isResizing && 'bg-black/[0.04]'
        )}
        style={{ height: PEZ_EFFECTS_LIST_RESIZE_HANDLE_HEIGHT }}
      >
        <GripHorizontal className="size-4" strokeWidth={2} aria-hidden="true" />
      </button>
    </div>
  )
}
